import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { N900 } from '@atlaskit/theme/colors';
import { AsyncResult } from '@atlassiansox/bxp-react-core';
import { CurrencyISOCode, FetchPricing } from '@atlassiansox/bxp-use-pricing';
import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { PlanCardsContainer, PricingExampleContainer } from './components';
import { HighestDisplayPriceFontSize } from './components/highest-display-price-font-size';
import {
  useBoundingClientRectRef,
  useDesktopMediaQuery,
  usePriceFinderPricingData
} from './hooks';
import { ProductPlan } from './models';
import { initialState, usePriceFinderStore } from './store';
import {
  DefaultPlanLimits,
  deviceMediaQueries,
  TRACKING_EVENT_NAMES,
  UNIT_TRACKING_TERMS
} from './utils/constants';
import { getRecommendedPlanName } from './utils/get-recommended-plan-name';

export type PriceFinderProps = {
  readonly productKey?: string;
  readonly productPlans: ProductPlan[];

  readonly signupURL?: string;
  readonly halpSignupURLs?: string[];
  readonly contactURL?: string;

  readonly currencyISOCode?: string;
  readonly localeISOCode?: string;
  readonly fetchPricing: FetchPricing;

  readonly defaultUnitCount?: number;

  readonly unitTrackingTerm?: UNIT_TRACKING_TERMS;

  readonly pricingExampleUnitCounts?: number[];
  readonly hideBillingSelector?: boolean;
  readonly unitLimitFree?: number;
  readonly unitMinEnterprise?: number;
  readonly showFirstFlatRateTier?: boolean;
};

const PriceFinder = ({
  productKey = '',
  productPlans,

  signupURL = '/try/cloud/signup',
  contactURL = '/contact/enterprise',

  defaultUnitCount = 10,
  pricingExampleUnitCounts = [300, 250],

  currencyISOCode = 'USD',
  localeISOCode = 'en-US',
  fetchPricing,

  hideBillingSelector = false,
  unitTrackingTerm = UNIT_TRACKING_TERMS.users,

  unitLimitFree = DefaultPlanLimits.UNIT_LIMIT_FREE,
  unitMinEnterprise = DefaultPlanLimits.UNIT_MIN_ENTERPRISE,

  showFirstFlatRateTier
}: PriceFinderProps) => {
  // setting this up here to take care of default unitCount for products like JSM, Bitbucket.
  initialState.unitCount = defaultUnitCount;

  const isDesktop = useDesktopMediaQuery();
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [{ unitCount, billingCycle }, actions] = usePriceFinderStore();
  const [fontSize, setFontSize] = useState('100%');

  useEffect(() => {
    void actions.updateUnitCount(defaultUnitCount);
  }, []);

  const [cardsContainerRect, cardRef] = useBoundingClientRectRef();

  // send analytics event if unit count has changed
  useEffect(() => {
    const analyticsEvent = createAnalyticsEvent({
      name: TRACKING_EVENT_NAMES.unitTierBox,
      unitBoxValue: unitCount
    });
    analyticsEvent.fire();
  }, [unitCount]);

  // send analytics event if billing cycle has changed
  useEffect(() => {
    const analyticsEvent = createAnalyticsEvent({
      name: TRACKING_EVENT_NAMES.billingCycleChanged,
      newPeriod: billingCycle
    });
    analyticsEvent.fire();
  }, [billingCycle]);

  const fetchPriceCalcs = usePriceFinderPricingData(
    pricingExampleUnitCounts,
    productKey,
    productPlans,
    currencyISOCode as CurrencyISOCode,
    localeISOCode,
    fetchPricing,
    unitMinEnterprise
  );

  const completedCalcsClone = AsyncResult.isComplete(fetchPriceCalcs)
    ? fetchPriceCalcs.resolve()
    : null;
  const completedCalcsRef = useRef(completedCalcsClone);
  completedCalcsRef.current = completedCalcsClone;

  if (AsyncResult.isPending(fetchPriceCalcs)) {
    return <div />; // no loading state currently
  } else if (AsyncResult.isComplete(fetchPriceCalcs)) {
    const completedCalcs = fetchPriceCalcs.resolve();
    void actions.updatePriceCalcs(completedCalcs);
  } else if (AsyncResult.isError(fetchPriceCalcs)) {
    throw fetchPriceCalcs.error;
  }

  const recommendedPlanName = getRecommendedPlanName(unitCount, productPlans);

  let cardWidth = cardsContainerRect
    ? Math.floor(cardsContainerRect.width / productPlans.length) * 0.75
    : -1;

  if (!isDesktop) {
    cardWidth = (cardsContainerRect && cardsContainerRect.width) || 200;
  }

  return (
    <PriceFinderContainer>
      <HighestDisplayPriceFontSize
        cardWidth={cardWidth}
        onFontSizeHandler={setFontSize}
      />

      <PlansAndPricingHeader>
        <FormattedMessage
          id="plansAndPricingTitle"
          defaultMessage="Compare plans and pricing"
          description="Plans and pricing title"
        />
      </PlansAndPricingHeader>

      <PricingExampleContainer
        unitTrackingTerm={unitTrackingTerm}
        pricingExampleUnitCounts={pricingExampleUnitCounts}
        hideBillingSelector={hideBillingSelector}
        showFirstFlatRateTier={showFirstFlatRateTier || false}
        currencyISOCode={currencyISOCode as CurrencyISOCode}
      />

      <PlanCardsContainer
        productKey={productKey}
        cardRef={cardRef}
        productPlans={productPlans}
        recommendedPlanName={recommendedPlanName}
        signupURL={signupURL}
        contactURL={contactURL}
        currencyISOCode={currencyISOCode as CurrencyISOCode}
        fontSize={fontSize}
        unitLimitFree={unitLimitFree}
        unitMinEnterprise={unitMinEnterprise}
      />
    </PriceFinderContainer>
  );
};

export const PriceFinderContainer = styled.div`
  color: ${N900};
  font-family: Charlie Text;

  width: 100%;
  h3 {
    font-family: Charlie Display;
    font-weight: 500;
  }

  h4 {
    font-family: Charlie Display;
    font-weight: normal;
  }
`;

const PlansAndPricingHeader = styled.h2`
  color: ${N900};
  font-family: Charlie Display;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  margin: 32px 0;
  text-align: center;

  ${deviceMediaQueries.desktop} {
    font-size: 36px;
    line-height: 44px;
    margin-bottom: 48px 0;
  }
`;

export default PriceFinder;
